import React, { useEffect, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import styled from '@emotion/styled/macro';
import PropTypes from 'prop-types';
import withFlatArgs from 'components/FinalForm/withFlatArgs';
import { workspaceActions, workspaceSelectors } from 'redux/workspace';
import computeStructuralUnitSuggestions from './structuralUnitSuggestions';

const GroupHeader = styled('div')`
  padding-top: 0.25rem;
  padding-right: 1rem;
  padding-bottom: 0.25rem;
  padding-left: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const OptionRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
  min-width: 0;
  gap: 0.5rem;
`;

const OptionPath = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionCount = styled('span')`
  color: #888;
  font-size: 0.85em;
  flex-shrink: 0;
`;

const GroupList = styled('ul')`
  padding: 0;
`;

const pathOf = (option) => (typeof option === 'string' ? option : option?.path);

const isOptionEqualToValue = (option, candidate) => (
  pathOf(option)?.toLowerCase() === pathOf(candidate)?.toLowerCase()
);

const nextLevelLabel = (path) => {
  const parts = path.split(' / ');
  return parts.length > 1 ? `… / ${parts[parts.length - 1]}` : path;
};

const StructuralUnitAutocomplete = React.forwardRef(({
  value, onChange, onBlur, onFocus,
  label, placeholder, error, helperText,
  ...rest
}, ref) => {
  const { t } = useTranslation('component');
  const dispatch = useDispatch();
  const list = useSelector(workspaceSelectors.getStructuralUnits);
  const status = useSelector(workspaceSelectors.getStructuralUnitsStatus);
  const currentValue = value ?? '';

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (status === 'idle') dispatch(workspaceActions.getStructuralUnits());
  }, [status, dispatch]);

  const options = useMemo(() => {
    const { nextLevel, complete } = computeStructuralUnitSuggestions(list, currentValue);
    return [...nextLevel, ...complete];
  }, [list, currentValue]);

  const hasNextLevel = options.some(option => option.section === 'nextLevel');

  const handleInputChange = (event, next, reason) => {
    if (reason === 'reset') return;
    onChange(next);
  };

  const handleChange = (event, selected) => {
    if (!selected) return;
    if (typeof selected === 'string') {
      onChange(selected);
      setOpen(false);
      return;
    }
    if (selected.section === 'nextLevel') {
      onChange(`${selected.path} / `);
    } else {
      onChange(selected.path);
      setOpen(false);
    }
  };

  const handleBlur = (event) => {
    const cleaned = currentValue.replace(/(\s*\/\s*)+$/, '');
    if (cleaned !== currentValue) onChange(cleaned);
    onBlur?.(event);
  };

  return (
    <Autocomplete
      ref={ref}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}

      value={currentValue}
      inputValue={currentValue}
      onInputChange={handleInputChange}
      onChange={handleChange}

      options={options}
      filterOptions={(givenOptions) => givenOptions}
      groupBy={(option) => option.section}
      getOptionLabel={pathOf}
      isOptionEqualToValue={isOptionEqualToValue}

      freeSolo
      fullWidth
      size="small"
      disableCloseOnSelect

      slotProps={{
        listbox: { sx: { maxHeight: '16rem' } },
      }}

      renderGroup={(params) => (
        <li key={params.key}>
          {params.group === 'complete' && hasNextLevel && (
            <>
              <Divider />
              <GroupHeader>{t('structuralUnitAutocomplete.existingHeader')}</GroupHeader>
            </>
          )}
          <GroupList>{params.children}</GroupList>
        </li>
      )}

      renderOption={(optionProps, option) => {
        const { key: muiKey, ...optionRest } = optionProps;
        const stableKey = `${option.section}-${option.path}`;
        return (
          <MenuItem dense key={stableKey || muiKey} {...optionRest}>
            <OptionRow>
              <OptionPath>{option.section === 'nextLevel' ? nextLevelLabel(option.path) : option.path}</OptionPath>
              <OptionCount>({option.entryCount ?? option.memberCount})</OptionCount>
            </OptionRow>
          </MenuItem>
        );
      }}

      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('structuralUnitAutocomplete.label')}
          placeholder={placeholder ?? t('structuralUnitAutocomplete.placeholder')}
          margin="none"
          autoComplete="off"
          error={error}
          helperText={helperText}
          onBlur={handleBlur}
          onFocus={onFocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                <InputAdornment position="end">
                  <Tooltip title={<Trans i18nKey="component:structuralUnitAutocomplete.tooltipText">.<br />.</Trans>}>
                    <IconButton size="small" sx={{ cursor: 'default' }} disableRipple>
                      <HelpOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              </>
            ),
          }}
        />
      )}

      {...rest}
    />
  );
});

StructuralUnitAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
};

const FinalFormComponent = withFlatArgs(StructuralUnitAutocomplete, 'StructuralUnitAutocomplete');
StructuralUnitAutocomplete.FinalForm = (args) => <Field component={FinalFormComponent} {...args} />;

export default StructuralUnitAutocomplete;
